type QrVersionConfig = {
  version: number;
  alignmentPatternCenters: number[];
  errorCorrectionCodewordsPerBlock: number;
  dataCodewordsPerBlock: number[];
};

type QrMatrix = boolean[][];

const QR_VERSION_CONFIGS: QrVersionConfig[] = [
  { version: 1, alignmentPatternCenters: [], errorCorrectionCodewordsPerBlock: 7, dataCodewordsPerBlock: [19] },
  { version: 2, alignmentPatternCenters: [6, 18], errorCorrectionCodewordsPerBlock: 10, dataCodewordsPerBlock: [34] },
  { version: 3, alignmentPatternCenters: [6, 22], errorCorrectionCodewordsPerBlock: 15, dataCodewordsPerBlock: [55] },
  { version: 4, alignmentPatternCenters: [6, 26], errorCorrectionCodewordsPerBlock: 20, dataCodewordsPerBlock: [80] },
  { version: 5, alignmentPatternCenters: [6, 30], errorCorrectionCodewordsPerBlock: 26, dataCodewordsPerBlock: [108] },
  { version: 6, alignmentPatternCenters: [6, 34], errorCorrectionCodewordsPerBlock: 18, dataCodewordsPerBlock: [68, 68] },
  { version: 7, alignmentPatternCenters: [6, 22, 38], errorCorrectionCodewordsPerBlock: 20, dataCodewordsPerBlock: [78, 78] },
  { version: 8, alignmentPatternCenters: [6, 24, 42], errorCorrectionCodewordsPerBlock: 24, dataCodewordsPerBlock: [97, 97] },
  { version: 9, alignmentPatternCenters: [6, 26, 46], errorCorrectionCodewordsPerBlock: 30, dataCodewordsPerBlock: [116, 116] },
  { version: 10, alignmentPatternCenters: [6, 28, 50], errorCorrectionCodewordsPerBlock: 18, dataCodewordsPerBlock: [68, 68, 69, 69] },
];

const BYTE_MODE_INDICATOR = 0b0100;
const ERROR_CORRECTION_FORMAT_BITS_L = 0b01;

const createMatrix = (size: number, value: boolean | null = null) => (
  Array.from({ length: size }, () => Array.from({ length: size }, () => value))
);

const appendBits = (target: number[], value: number, length: number) => {
  for (let i = length - 1; i >= 0; i -= 1) {
    target.push((value >>> i) & 1);
  }
};

const getBit = (value: number, index: number) => ((value >>> index) & 1) !== 0;

const getDataCodewordCount = (config: QrVersionConfig) => (
  config.dataCodewordsPerBlock.reduce((sum, count) => sum + count, 0)
);

const getByteCapacity = (config: QrVersionConfig) => {
  const characterCountBits = config.version <= 9 ? 8 : 16;
  const dataBits = getDataCodewordCount(config) * 8;
  return Math.floor((dataBits - 4 - characterCountBits - 4) / 8);
};

const selectVersionConfig = (bytes: Uint8Array) => {
  const config = QR_VERSION_CONFIGS.find((item) => bytes.length <= getByteCapacity(item));
  if (!config) {
    throw new Error('Texto grande demais para gerar QR Code localmente.');
  }
  return config;
};

const createDataCodewords = (bytes: Uint8Array, config: QrVersionConfig) => {
  const dataCodewordCount = getDataCodewordCount(config);
  const dataBitCapacity = dataCodewordCount * 8;
  const characterCountBits = config.version <= 9 ? 8 : 16;
  const bits: number[] = [];

  appendBits(bits, BYTE_MODE_INDICATOR, 4);
  appendBits(bits, bytes.length, characterCountBits);
  bytes.forEach((byte) => appendBits(bits, byte, 8));
  appendBits(bits, 0, Math.min(4, dataBitCapacity - bits.length));

  while (bits.length % 8 !== 0) {
    bits.push(0);
  }

  const codewords: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    let value = 0;
    for (let j = 0; j < 8; j += 1) {
      value = (value << 1) | bits[i + j];
    }
    codewords.push(value);
  }

  for (let pad = 0xec; codewords.length < dataCodewordCount; pad ^= 0xfd) {
    codewords.push(pad);
  }

  return codewords;
};

const GF_EXP: number[] = [];
const GF_LOG: number[] = [];

(() => {
  let value = 1;
  for (let i = 0; i < 255; i += 1) {
    GF_EXP[i] = value;
    GF_LOG[value] = i;
    value <<= 1;
    if (value & 0x100) {
      value ^= 0x11d;
    }
  }

  for (let i = 255; i < 512; i += 1) {
    GF_EXP[i] = GF_EXP[i - 255];
  }
})();

const reedSolomonMultiply = (a: number, b: number) => (
  a === 0 || b === 0 ? 0 : GF_EXP[GF_LOG[a] + GF_LOG[b]]
);

const reedSolomonGenerator = (degree: number) => {
  const result: number[] = Array.from({ length: degree }, (_, index) => (index === degree - 1 ? 1 : 0));
  let root = 1;

  for (let i = 0; i < degree; i += 1) {
    for (let j = 0; j < result.length; j += 1) {
      result[j] = reedSolomonMultiply(result[j], root);
      if (j + 1 < result.length) {
        result[j] ^= result[j + 1];
      }
    }
    root = reedSolomonMultiply(root, 0x02);
  }

  return result;
};

const reedSolomonRemainder = (data: number[], degree: number) => {
  const generator = reedSolomonGenerator(degree);
  const result = Array.from({ length: degree }, () => 0);

  data.forEach((codeword) => {
    const factor = codeword ^ result.shift()!;
    result.push(0);

    generator.forEach((coefficient, index) => {
      result[index] ^= reedSolomonMultiply(coefficient, factor);
    });
  });

  return result;
};

const addErrorCorrectionAndInterleave = (dataCodewords: number[], config: QrVersionConfig) => {
  let offset = 0;
  const blocks = config.dataCodewordsPerBlock.map((length) => {
    const data = dataCodewords.slice(offset, offset + length);
    offset += length;
    return {
      data,
      errorCorrection: reedSolomonRemainder(data, config.errorCorrectionCodewordsPerBlock),
    };
  });

  const result: number[] = [];
  const maxDataLength = Math.max(...blocks.map((block) => block.data.length));

  for (let i = 0; i < maxDataLength; i += 1) {
    blocks.forEach((block) => {
      if (i < block.data.length) {
        result.push(block.data[i]);
      }
    });
  }

  for (let i = 0; i < config.errorCorrectionCodewordsPerBlock; i += 1) {
    blocks.forEach((block) => {
      result.push(block.errorCorrection[i]);
    });
  }

  return result;
};

const createEmptyQr = (size: number) => ({
  modules: createMatrix(size, false) as boolean[][],
  isFunction: createMatrix(size, false) as boolean[][],
});

const setFunctionModule = (
  modules: boolean[][],
  isFunction: boolean[][],
  x: number,
  y: number,
  dark: boolean,
) => {
  if (y < 0 || y >= modules.length || x < 0 || x >= modules.length) return;
  modules[y][x] = dark;
  isFunction[y][x] = true;
};

const drawFinderPattern = (
  modules: boolean[][],
  isFunction: boolean[][],
  left: number,
  top: number,
) => {
  for (let dy = -1; dy <= 7; dy += 1) {
    for (let dx = -1; dx <= 7; dx += 1) {
      const x = left + dx;
      const y = top + dy;
      const isFinderBody = dx >= 0 && dx <= 6 && dy >= 0 && dy <= 6;
      const dark = isFinderBody && (
        dx === 0 || dx === 6 || dy === 0 || dy === 6
        || (dx >= 2 && dx <= 4 && dy >= 2 && dy <= 4)
      );
      setFunctionModule(modules, isFunction, x, y, dark);
    }
  }
};

const drawAlignmentPattern = (
  modules: boolean[][],
  isFunction: boolean[][],
  centerX: number,
  centerY: number,
) => {
  for (let dy = -2; dy <= 2; dy += 1) {
    for (let dx = -2; dx <= 2; dx += 1) {
      const dark = Math.max(Math.abs(dx), Math.abs(dy)) !== 1;
      setFunctionModule(modules, isFunction, centerX + dx, centerY + dy, dark);
    }
  }
};

const reserveFormatAreas = (modules: boolean[][], isFunction: boolean[][]) => {
  const size = modules.length;

  for (let i = 0; i <= 5; i += 1) {
    setFunctionModule(modules, isFunction, 8, i, false);
    setFunctionModule(modules, isFunction, i, 8, false);
  }

  setFunctionModule(modules, isFunction, 8, 7, false);
  setFunctionModule(modules, isFunction, 8, 8, false);
  setFunctionModule(modules, isFunction, 7, 8, false);

  for (let i = 0; i < 8; i += 1) {
    setFunctionModule(modules, isFunction, size - 1 - i, 8, false);
  }

  for (let i = 8; i < 15; i += 1) {
    setFunctionModule(modules, isFunction, 8, size - 15 + i, false);
  }
};

const getFormatBits = (mask: number) => {
  const data = (ERROR_CORRECTION_FORMAT_BITS_L << 3) | mask;
  let remainder = data;

  for (let i = 0; i < 10; i += 1) {
    remainder = (remainder << 1) ^ (((remainder >>> 9) & 1) * 0x537);
  }

  return ((data << 10) | remainder) ^ 0x5412;
};

const drawFormatBits = (modules: boolean[][], isFunction: boolean[][], mask: number) => {
  const size = modules.length;
  const bits = getFormatBits(mask);

  for (let i = 0; i <= 5; i += 1) {
    setFunctionModule(modules, isFunction, 8, i, getBit(bits, i));
  }

  setFunctionModule(modules, isFunction, 8, 7, getBit(bits, 6));
  setFunctionModule(modules, isFunction, 8, 8, getBit(bits, 7));
  setFunctionModule(modules, isFunction, 7, 8, getBit(bits, 8));

  for (let i = 9; i < 15; i += 1) {
    setFunctionModule(modules, isFunction, 14 - i, 8, getBit(bits, i));
  }

  for (let i = 0; i < 8; i += 1) {
    setFunctionModule(modules, isFunction, size - 1 - i, 8, getBit(bits, i));
  }

  for (let i = 8; i < 15; i += 1) {
    setFunctionModule(modules, isFunction, 8, size - 15 + i, getBit(bits, i));
  }

  setFunctionModule(modules, isFunction, 8, size - 8, true);
};

const getVersionBits = (version: number) => {
  let remainder = version;

  for (let i = 0; i < 12; i += 1) {
    remainder = (remainder << 1) ^ (((remainder >>> 11) & 1) * 0x1f25);
  }

  return (version << 12) | remainder;
};

const drawVersionBits = (modules: boolean[][], isFunction: boolean[][], version: number) => {
  if (version < 7) return;

  const size = modules.length;
  const bits = getVersionBits(version);

  for (let i = 0; i < 18; i += 1) {
    const dark = getBit(bits, i);
    const a = size - 11 + (i % 3);
    const b = Math.floor(i / 3);
    setFunctionModule(modules, isFunction, a, b, dark);
    setFunctionModule(modules, isFunction, b, a, dark);
  }
};

const drawFunctionPatterns = (modules: boolean[][], isFunction: boolean[][], config: QrVersionConfig) => {
  const size = modules.length;

  drawFinderPattern(modules, isFunction, 0, 0);
  drawFinderPattern(modules, isFunction, size - 7, 0);
  drawFinderPattern(modules, isFunction, 0, size - 7);

  for (let i = 8; i < size - 8; i += 1) {
    const dark = i % 2 === 0;
    setFunctionModule(modules, isFunction, i, 6, dark);
    setFunctionModule(modules, isFunction, 6, i, dark);
  }

  config.alignmentPatternCenters.forEach((centerY) => {
    config.alignmentPatternCenters.forEach((centerX) => {
      const overlapsFinderPattern = (
        (centerX <= 8 && centerY <= 8)
        || (centerX >= size - 9 && centerY <= 8)
        || (centerX <= 8 && centerY >= size - 9)
      );

      if (!overlapsFinderPattern) {
        drawAlignmentPattern(modules, isFunction, centerX, centerY);
      }
    });
  });

  setFunctionModule(modules, isFunction, 8, size - 8, true);
  reserveFormatAreas(modules, isFunction);
  drawVersionBits(modules, isFunction, config.version);
};

const getMaskValue = (mask: number, x: number, y: number) => {
  switch (mask) {
    case 0: return (x + y) % 2 === 0;
    case 1: return y % 2 === 0;
    case 2: return x % 3 === 0;
    case 3: return (x + y) % 3 === 0;
    case 4: return (Math.floor(y / 2) + Math.floor(x / 3)) % 2 === 0;
    case 5: return ((x * y) % 2) + ((x * y) % 3) === 0;
    case 6: return (((x * y) % 2) + ((x * y) % 3)) % 2 === 0;
    case 7: return (((x + y) % 2) + ((x * y) % 3)) % 2 === 0;
    default: return false;
  }
};

const drawCodewords = (modules: boolean[][], isFunction: boolean[][], codewords: number[]) => {
  const size = modules.length;
  const bits: number[] = [];
  codewords.forEach((codeword) => appendBits(bits, codeword, 8));

  let bitIndex = 0;
  let upward = true;

  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) {
      right -= 1;
    }

    for (let vertical = 0; vertical < size; vertical += 1) {
      const y = upward ? size - 1 - vertical : vertical;

      for (let columnOffset = 0; columnOffset < 2; columnOffset += 1) {
        const x = right - columnOffset;
        if (isFunction[y][x]) continue;

        modules[y][x] = bitIndex < bits.length ? bits[bitIndex] === 1 : false;
        bitIndex += 1;
      }
    }

    upward = !upward;
  }
};

const applyMask = (modules: boolean[][], isFunction: boolean[][], mask: number) => {
  modules.forEach((row, y) => {
    row.forEach((_, x) => {
      if (!isFunction[y][x] && getMaskValue(mask, x, y)) {
        modules[y][x] = !modules[y][x];
      }
    });
  });
};

const cloneMatrix = (matrix: boolean[][]) => matrix.map((row) => [...row]);

const getPenaltyScore = (modules: boolean[][]) => {
  const size = modules.length;
  let penalty = 0;

  for (let y = 0; y < size; y += 1) {
    let runColor = modules[y][0];
    let runLength = 1;

    for (let x = 1; x < size; x += 1) {
      if (modules[y][x] === runColor) {
        runLength += 1;
      } else {
        if (runLength >= 5) penalty += runLength - 2;
        runColor = modules[y][x];
        runLength = 1;
      }
    }
    if (runLength >= 5) penalty += runLength - 2;
  }

  for (let x = 0; x < size; x += 1) {
    let runColor = modules[0][x];
    let runLength = 1;

    for (let y = 1; y < size; y += 1) {
      if (modules[y][x] === runColor) {
        runLength += 1;
      } else {
        if (runLength >= 5) penalty += runLength - 2;
        runColor = modules[y][x];
        runLength = 1;
      }
    }
    if (runLength >= 5) penalty += runLength - 2;
  }

  for (let y = 0; y < size - 1; y += 1) {
    for (let x = 0; x < size - 1; x += 1) {
      const color = modules[y][x];
      if (
        modules[y][x + 1] === color
        && modules[y + 1][x] === color
        && modules[y + 1][x + 1] === color
      ) {
        penalty += 3;
      }
    }
  }

  const finderLikePattern = [true, false, true, true, true, false, true, false, false, false, false];
  const finderLikePatternReverse = [false, false, false, false, true, false, true, true, true, false, true];

  const matchesPattern = (values: boolean[]) => (
    values.every((value, index) => value === finderLikePattern[index])
    || values.every((value, index) => value === finderLikePatternReverse[index])
  );

  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x <= size - 11; x += 1) {
      if (matchesPattern(modules[y].slice(x, x + 11))) {
        penalty += 40;
      }
    }
  }

  for (let x = 0; x < size; x += 1) {
    for (let y = 0; y <= size - 11; y += 1) {
      const values = Array.from({ length: 11 }, (_, index) => modules[y + index][x]);
      if (matchesPattern(values)) {
        penalty += 40;
      }
    }
  }

  const darkModules = modules.flat().filter(Boolean).length;
  const totalModules = size * size;
  const darkPercent = (darkModules * 100) / totalModules;
  penalty += Math.floor(Math.abs(darkPercent - 50) / 5) * 10;

  return penalty;
};

const createQrMatrix = (text: string): QrMatrix => {
  const bytes = new TextEncoder().encode(text);
  const config = selectVersionConfig(bytes);
  const size = 17 + config.version * 4;
  const { modules, isFunction } = createEmptyQr(size);
  const dataCodewords = createDataCodewords(bytes, config);
  const codewords = addErrorCorrectionAndInterleave(dataCodewords, config);

  drawFunctionPatterns(modules, isFunction, config);
  drawCodewords(modules, isFunction, codewords);

  const baseModules = cloneMatrix(modules);
  let bestMatrix = cloneMatrix(baseModules);
  let bestPenalty = Number.POSITIVE_INFINITY;

  for (let mask = 0; mask < 8; mask += 1) {
    const candidate = cloneMatrix(baseModules);
    applyMask(candidate, isFunction, mask);
    drawFormatBits(candidate, isFunction, mask);

    const penalty = getPenaltyScore(candidate);
    if (penalty < bestPenalty) {
      bestPenalty = penalty;
      bestMatrix = candidate;
    }
  }

  return bestMatrix;
};

export const createQrCodeSvg = (text: string, margin = 4) => {
  const matrix = createQrMatrix(text);
  const moduleCount = matrix.length + margin * 2;
  const darkPath = matrix
    .flatMap((row, y) => (
      row
        .map((isDark, x) => (isDark ? `M${x + margin} ${y + margin}h1v1h-1z` : ''))
        .filter(Boolean)
    ))
    .join('');

  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${moduleCount}" height="${moduleCount}" viewBox="0 0 ${moduleCount} ${moduleCount}" shape-rendering="crispEdges">`,
    `<path fill="#ffffff" d="M0 0h${moduleCount}v${moduleCount}H0z"/>`,
    `<path fill="#111827" d="${darkPath}"/>`,
    '</svg>',
  ].join('');
};

export const createQrCodeSvgDataUrl = (text: string, margin = 4) => (
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(createQrCodeSvg(text, margin))}`
);

export const createQrCodePngBlob = (
  text: string,
  size = 720,
  margin = 4,
) => new Promise<Blob>((resolve, reject) => {
  const svg = createQrCodeSvg(text, margin);
  const image = new Image();
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    reject(new Error('Canvas indisponível para gerar QR Code.'));
    return;
  }

  image.onload = () => {
    canvas.width = size;
    canvas.height = size;
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, size, size);
    context.drawImage(image, 0, 0, size, size);
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Não foi possível exportar o QR Code.'));
      }
    }, 'image/png');
  };

  image.onerror = () => reject(new Error('Não foi possível renderizar o QR Code.'));
  image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
});
